import React from "react";
import { Box, Button, Checkbox, Chip, FormControlLabel, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IssueStatus } from "../../../../logic/api";
import { getStatusLabel, getStatusColorMui } from "./issueUtils";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";

interface IssueStatusFilterProps {
  selectedStatuses: IssueStatus[];
  onStatusChange: (statuses: IssueStatus[]) => void;
}


const IssueStatusFilter: React.FunctionComponent<IssueStatusFilterProps> = ({
  selectedStatuses,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const isNewFilterUiEnabled = useReleaseManagement(ReleaseFlag.releaseChangeFilterUiVehicleIssues);

  const allStatuses = [IssueStatus.Open, IssueStatus.Assigned, IssueStatus.Resolved, IssueStatus.NoAction];

  const handleStatusToggle = (status: IssueStatus) => {
    const newSelectedStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter(s => s !== status)
      : [...selectedStatuses, status];

    onStatusChange(newSelectedStatuses);
  };

  const handleClearAll = () => {
    onStatusChange([]);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", m: 2 }}>
      <Typography variant="body2" sx={{ mr: 1 }}>
        {t("locations.vehicleIssues.filters.title")}:
      </Typography>

      {isNewFilterUiEnabled ? (
        allStatuses.map((status) => (
          <FormControlLabel
            key={status}
            control={
              <Checkbox
                data-testid={`status-filter-${status}`}
                checked={selectedStatuses.includes(status)}
                onChange={() => handleStatusToggle(status)}
                color="secondary"
                size="small"
              />
            }
            label={getStatusLabel(status, t)}
          />
        ))
      ) : (
        <>
          {allStatuses.map((status) => (
            <Chip
              key={status}
              data-testid={`status-filter-${status}`}
              label={getStatusLabel(status, t)}
              color={getStatusColorMui(status)}
              variant={selectedStatuses.includes(status) ? "filled" : "outlined"}
              onClick={() => handleStatusToggle(status)}
              size="small"
            />
          ))}
          <Button
            data-testid="status-filter-clear-all"
            variant="text"
            onClick={handleClearAll}
            size="small"
            sx={{ ml: 1 }}
          >
            {t("locations.vehicleIssues.filters.clearAll")}
          </Button>
        </>
      )}
    </Box>
  );
};

export default IssueStatusFilter;